/**
 * "Mentioned in" backlinks — the inbound side of the cross-reference web
 * (§4.10 / §4.15). Given the wire backlink list for a selected element (or a
 * document), render a compact list of the elements + documents that reference
 * it. Clicking a row routes through the app's existing `archlang://` nav so the
 * board / docs surface focuses the referencing entity.
 *
 * Self-contained and presentational: the caller supplies the resolved list
 * (via `lookupBacklinks`) and an `onNavigate` that takes an `archlang://` href
 * (the same handler the rendered descriptions use). Renders nothing when the
 * list is empty so it can be dropped into any details panel unconditionally.
 */

import { Tooltip, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import type { WireBacklink } from "@archlang/viewer-core";
import { useTranslation } from "../i18n";

/** The `archlang://` href that focuses a backlink's source entity, reusing the
 *  app's existing link router. Elements route by qualified name; documents by
 *  document name. */
function backlinkHref(b: WireBacklink): string {
  if (b.kind === "document") {
    return `archlang://doc/${encodeURIComponent(b.name)}`;
  }
  return `archlang://name/${encodeURIComponent(b.qualifiedName ?? b.name)}`;
}

interface BacklinksProps {
  readonly backlinks: readonly WireBacklink[];
  /** Routes a clicked backlink — given its `archlang://` href — to a
   *  selection / mode change. Usually the app's `handleArchlangNav`. */
  readonly onNavigate?: (href: string) => void;
  /** Tailwind classes for the outer wrapper; defaults to sidebar spacing. */
  readonly className?: string;
}

export function Backlinks({ backlinks, onNavigate, className }: BacklinksProps) {
  const { t } = useTranslation("nav");
  const menu = useContextMenu();
  if (backlinks.length === 0) return null;

  const itemsFor = (b: WireBacklink): ContextMenuItem[] => [
    ...(onNavigate
      ? [{ label: t("menu.open"), onClick: () => onNavigate(backlinkHref(b)) }]
      : []),
    {
      label: t("menu.copyReference"),
      separator: true,
      onClick: () =>
        void navigator.clipboard?.writeText(b.qualifiedName ?? b.name),
    },
    {
      label: t("menu.copyName"),
      onClick: () => void navigator.clipboard?.writeText(b.name),
    },
  ];

  return (
    <div className={className}>
      <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1.5 tracking-wide">
        Mentioned in ({backlinks.length})
      </h3>
      <ul className="space-y-1">
        {backlinks.map((b, i) => (
          <li
            key={`${b.kind}-${b.qualifiedName ?? b.name}-${i}`}
            onContextMenu={(e) => menu.open(e, itemsFor(b))}
          >
            <Tooltip content={b.qualifiedName ?? b.path ?? b.name}>
              <button
                type="button"
                onClick={onNavigate ? () => onNavigate(backlinkHref(b)) : undefined}
                disabled={!onNavigate}
                className={
                  "w-full text-left rounded-sm bg-slate-900/(--tint-muted) border border-slate-700/(--tint-strong) px-2 py-1.5 flex items-center gap-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
                  (onNavigate
                    ? "hover:bg-slate-700 hover:border-sky-500/50 cursor-pointer"
                    : "cursor-default")
                }
              >
                <span
                  className={
                    "text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm shrink-0 " +
                    (b.kind === "document"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-sky-500/15 text-sky-300")
                  }
                >
                  {b.kind === "document" ? "doc" : "ref"}
                </span>
                <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">
                  {b.name}
                </span>
              </button>
            </Tooltip>
          </li>
        ))}
      </ul>
      {menu.node}
    </div>
  );
}
